import React, { useState, useEffect } from 'react';
import { apiService, Operatory } from '../../services/apiService';

const emptyOperatory: Partial<Operatory> = {
  name: '',
  description: '',
  is_active: true,
};

const OperatoriesPage: React.FC = () => {
  const [operatories, setOperatories] = useState<Operatory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Operatory> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getOperatories(false);
      setOperatories(r.data.data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load operatories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async () => {
    if (!editing) return;
    if (!editing.name) {
      setError('Name is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing.id) {
        await apiService.updateOperatory(editing.id, editing);
      } else {
        await apiService.createOperatory(editing);
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (o: Operatory) => {
    if (!window.confirm(`Delete operatory "${o.name}"?`)) return;
    try {
      await apiService.deleteOperatory(o.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Operatories</h2>
        <button
          onClick={() => setEditing({ ...emptyOperatory })}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-navy-admin hover:bg-blue-900"
        >
          + Add Room
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded">{error}</div>}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : operatories.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No rooms yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {operatories.map(o => (
                  <tr key={o.id} className={o.is_active ? '' : 'opacity-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{o.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{o.description}</td>
                    <td className="px-4 py-3 text-sm">
                      {o.is_active
                        ? <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
                        : <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">Inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm space-x-2 whitespace-nowrap">
                      <button onClick={() => setEditing({ ...o })} className="text-blue-600 hover:text-blue-900">Edit</button>
                      <button onClick={() => onDelete(o)} className="text-red-600 hover:text-red-900">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-bold mb-4">{editing.id ? 'Edit Room' : 'New Room'}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g. Room 1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-navy-admin" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })}
                  rows={2}
                  placeholder="e.g. Equipment, location notes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-navy-admin" />
              </div>
              <label className="flex items-center space-x-2 text-sm">
                <input type="checkbox" checked={!!editing.is_active}
                  onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                <span>Active</span>
              </label>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button onClick={() => setEditing(null)} disabled={saving}
                className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={onSave} disabled={saving}
                className="px-4 py-2 bg-navy-admin text-white rounded hover:bg-blue-900 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatoriesPage;
